import { useState, type ReactNode } from 'react';
import { reasonsForScope } from '@domain/entities/RuntimeConfig';
import type { Incapacity } from '@domain/entities/Incapacity';
import type { PermissionRequest } from '@domain/entities/PermissionRequest';
import { getServices } from '@infrastructure/services';
import { Button, Field, Modal } from '../../design-system';
import { formatDateRange } from '../../format';
import { useSession } from '../../providers/SessionProvider';
import { useToast } from '../../providers/ToastProvider';
import shared from '../shared.module.css';
import { ReasonPicker } from '../ReasonPicker';

/**
 * The decision flows for a permit and for a certificate.
 *
 * They live beside the board rather than inside the personal screens they came
 * from. «Mis ausencias» is the viewer's own records now, and nobody approves
 * their own permit — leaving the buttons there would have let a leader do exactly
 * that, since their own permit sits in their own list.
 *
 * Both refuse free text. The reason comes from the runtime catalogue, scoped to
 * `permiso` or `incapacidad`, for the same reason a vacation rejection does: a
 * log of "Rechazado" with no cause cannot be read by anyone six months later, and
 * a reason nobody can aggregate cannot be reported on at all. The callables
 * re-check the catalogue, so this is the UI half of a rule enforced in both
 * places.
 */

export const decidePermission = async (
  permission: PermissionRequest,
  decision: 'approve' | 'reject',
  reason: string | null,
): Promise<boolean> => {
  const result = await getServices().permissions.decide({
    id: permission.id,
    decision,
    reason,
    suggestedRanges: [],
  });
  return result.ok;
};

export const validateIncapacity = async (incapacity: Incapacity): Promise<boolean> => {
  const result = await getServices().incapacities.validate(incapacity.id);
  return result.ok;
};

/** Same catalogue-bound reason rule as a vacation rejection, scoped to `permiso`. */
export const RejectPermissionDialog = ({
  permission,
  onClose,
  onDone,
}: {
  permission: PermissionRequest;
  onClose: () => void;
  onDone: () => Promise<void>;
}): ReactNode => {
  const session = useSession();
  const toast = useToast();
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);

  const reasons = reasonsForScope(session.config.catalogs, 'permiso');

  return (
    <Modal
      title={`Rechazar ${permission.folio}`}
      onClose={onClose}
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            Volver
          </Button>
          <Button
            variant="danger"
            busy={busy}
            disabled={reason.length === 0}
            onClick={() => {
              setBusy(true);
              void decidePermission(permission, 'reject', reason).then(async (ok) => {
                setBusy(false);
                if (!ok) {
                  toast.bad('No se pudo registrar el rechazo.');
                  return;
                }
                toast.warn(`${permission.folio} rechazado.`);
                await onDone();
              });
            }}
          >
            Rechazar
          </Button>
        </>
      }
    >
      <div className={shared.stack}>
        <p className={shared.rowNote}>
          {permission.personName} pidió {permission.kind.toLowerCase()} el{' '}
          {formatDateRange(permission.range)}.
        </p>
        <Field label="Motivo del rechazo">
          {(id) => (
            <ReasonPicker id={id} reasons={reasons} value={reason} onChange={setReason} />
          )}
        </Field>
      </div>
    </Modal>
  );
};

/** Returns the certificate to the employee with a catalogue-bound observation. */
export const ObserveIncapacityDialog = ({
  incapacity,
  onClose,
  onDone,
}: {
  incapacity: Incapacity;
  onClose: () => void;
  onDone: () => Promise<void>;
}): ReactNode => {
  const session = useSession();
  const toast = useToast();
  const [observation, setObservation] = useState('');
  const [busy, setBusy] = useState(false);

  const reasons = reasonsForScope(session.config.catalogs, 'incapacidad');

  return (
    <Modal
      title={`Observar ${incapacity.folio}`}
      onClose={onClose}
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            Volver
          </Button>
          <Button
            busy={busy}
            disabled={observation.length === 0}
            onClick={() => {
              setBusy(true);
              void getServices()
                .incapacities.observe(incapacity.id, observation)
                .then(async (result) => {
                  setBusy(false);
                  if (!result.ok) {
                    toast.bad('No se pudo registrar la observación.');
                    return;
                  }
                  toast.warn(`${incapacity.folio} quedó observada.`);
                  await onDone();
                });
            }}
          >
            Registrar observación
          </Button>
        </>
      }
    >
      <div className={shared.stack}>
        <p className={shared.rowNote}>
          La persona verá la observación y podrá reenviar el certificado corregido.
        </p>
        <Field label="Motivo de la observación">
          {(id) => (
            <ReasonPicker id={id} reasons={reasons} value={observation} onChange={setObservation} />
          )}
        </Field>
      </div>
    </Modal>
  );
};

/** Undoing an approved permit so it returns to the leader's queue. */
export const ReopenPermitDialog = ({
  permission,
  onClose,
  onDone,
}: {
  permission: PermissionRequest;
  onClose: () => void;
  onDone: () => Promise<void>;
}): ReactNode => {
  const session = useSession();
  const toast = useToast();
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);

  const reasons = reasonsForScope(session.config.catalogs, 'permiso');

  return (
    <Modal
      title={`Reabrir ${permission.folio}`}
      onClose={onClose}
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            Volver
          </Button>
          <Button
            busy={busy}
            disabled={reason.length === 0}
            onClick={() => {
              setBusy(true);
              void getServices()
                .permissions.reopen(permission.id, reason)
                .then(async (result) => {
                  setBusy(false);
                  if (!result.ok) {
                    toast.bad('No se pudo reabrir el permiso.');
                    return;
                  }
                  toast.warn(`${permission.folio} volvió a Por aprobar.`);
                  await onDone();
                });
            }}
          >
            Reabrir
          </Button>
        </>
      }
    >
      <div className={shared.stack}>
        {/* Names the outcome it is coming out of rather than assuming an approval:
            a rejected permit reopens too, and «tenía aprobado» would be a lie. */}
        <p className={shared.rowNote}>
          El permiso de {permission.personName} está {permission.status.toLowerCase()} ·{' '}
          {formatDateRange(permission.range)}. Vuelve a la bandeja sin decisión, para que se
          resuelva otra vez.
        </p>
        <Field label="Motivo de la reapertura">
          {(id) => (
            <ReasonPicker id={id} reasons={reasons} value={reason} onChange={setReason} />
          )}
        </Field>
      </div>
    </Modal>
  );
};
